import React, { useEffect } from "react";
import { useFormikContext } from "formik";
import FieldGroup from "../FieldGroup";
import FormSection from "../FormSection";
import BodyEl from "../BodyEl";
import {
  CheckBox,
  Dropdown,
  File,
  Radio,
  TextArea,
  TextField,
  SurveyTextarea,
} from "../Input";
import RatingScale from "../Input/RatingScale";
import Canvas from "../Input/Canvas";
import PhoneInput from "../Input/PhoneInput";
import StarRating from "../StarRating/StarRating.jsx";
import ScaleRating from "../ScaleRating/ScaleRting.jsx";
import Distance from "../Distance/Distance.jsx";
import Dimension from "../Dimension/Dimension.jsx";
import Weight from "../Weight/Weight.jsx";

// Field.jsx
import NpsScale from "../Input/NpsScale/NpsScale";
import SearchSelect from "../Input/SearchSelect";
import { getName, getLastItemOrBefore, isFieldVisible } from "../../utils";

const Field = ({ field, parent, isSurvey = false }) => {
  const { getFieldProps, setFieldValue, values } = useFormikContext();

  // Fields tagged with `revealedBy` (see resolveFieldTriggers) start hidden
  // and only render once one of their listed source fields currently holds
  // the value that triggers them (OR across entries). Same check drives
  // applyVisibilityToSchema so a hidden required field never blocks submit.
  const isVisible = isFieldVisible(field, values);

  const ownFieldName = field.field_label
    ? getLastItemOrBefore(getName(parent, field.field_label))
    : "";

  useEffect(() => {
    if (isVisible || !ownFieldName) return;
    const current = getFieldProps(ownFieldName).value;
    if (current && typeof current === "object" && !Array.isArray(current)) return; // skip composite/group values
    if (Array.isArray(current) ? current.length === 0 : !current) return; // already empty
    setFieldValue(ownFieldName, Array.isArray(current) ? [] : "");
  }, [isVisible]);

  if (!isVisible) return null;

  if (field.field_type?.includes("sect-title"))
    return (
      <FormSection title={field.displayLabel || field.field_label} isRequired={field.is_required} />
    );
  if (field.field_type?.includes("body"))
    return (
      <BodyEl title={field.displayLabel || field.field_label} body={field.body} />
    );

  // NPS check — only in survey context, BEFORE scale and checkbox
  if (
    isSurvey &&
    field.field_type?.includes("checkbox") &&
    field.field_items?.every((item) => !isNaN(item))
  ) {
    return (
      <NpsScale
        field_key={field.field_label} // sanitized label → Formik key ✅
        field_label={field.field_label}
        displayLabel={field.displayLabel}
        isRequired={field.is_required === "1" || field.is_required === true}
        min={0}
        max={10}
      />
    );
  }
  // Auto-detect horizontal rating scale (e.g. 0-10)
  if (
    field.field_type?.includes("scale") ||
    field.field_label?.includes("0 - 10") ||
    field.field_label?.includes("Scale of 0 - 10")
  ) {
    return (
      <RatingScale parent={parent} {...field} isRequired={field.is_required} />
    );
  }

  if (field.field_type?.includes("textarea")) {
    if (isSurvey) {
      return (
        <SurveyTextarea
          parent={parent}
          {...field}
          isRequired={field.is_required}
        />
      );
    }
    return (
      <TextArea parent={parent} {...field} isRequired={field.is_required} charactersRange={field.characters_range} />
    );
  }
  // ... existing code ...

  if (field.field_type?.includes("checkbox"))
    return (
      <CheckBox parent={parent} {...field} isRequired={field.is_required} />
    );

  if (field.field_type?.includes("dropdown") || field.field_key === "dropdown")
    return (
      <div>
        <SearchSelect parent={parent} {...field} isRequired={field.is_required} />
        {/* <Dropdown parent={parent} {...field} isRequired={field.is_required} /> */}
      </div>
    );
  if (field.field_type == "business-sector" || field.field_type.includes('business-sector'))
    return (
      <Dropdown parent={parent} {...field} isRequired={field.is_required} />
    );
  if (field.field_type?.includes("radio")){
    return <Radio parent={parent} {...field} isRequired={field.is_required} />;
  }

  if (field.field_type?.includes("file"))
    return <File parent={parent} {...field} isRequired={field.is_required} />;

  if (
    field.field_type?.includes("number") &&
    field.field_label.includes("phone_number")
  ) {
    return (
      <PhoneInput 
        parent={parent} 
        {...field} 
        isRequired={field.is_required} 
      />
    );
  }

  if (
    field.field_type?.includes("input") ||
    field.field_type?.includes("number") ||
    field.field_type?.includes("date") ||
    field.field_type?.includes("currency")
  )
    return (
      <TextField parent={parent} {...field} isRequired={field.is_required} />
    );

  if (field.field_type?.includes("signature"))
  return <Canvas parent={parent} {...field} />;

  if(field?.field_type?.includes("star"))
    return <StarRating parent={parent} isRequired={field?.is_required} {...field} />

  if(field?.field_type?.includes("scale"))
    return <ScaleRating parent={parent} isRequired={field?.is_required} {...field} />
  
  if(field?.field_type?.includes("dimension"))
    return <Dimension parent={parent} isRequired={field?.is_required} {...field} />

  if(field?.field_type?.includes("distance"))
    return <Distance parent={parent} isRequired={field?.is_required} {...field} />

  if(field?.field_type?.includes("weight"))
    return <Weight parent={parent} isRequired={field?.is_required} {...field} />

  return <FieldGroup parent={parent} {...field} />;
};

export default Field;
